import React from 'react'
import { PeriodSummary, PeriodSummaryStatus } from '../../api/period-summaries'
import { Column } from 'react-table'
import { ResourceLinkCell } from '../../components/table/cells'
import { Button, Element, Icon, Level, Tag } from 'react-bulma-components'
import Table from '../../components/table'
import { localDate } from '../../utils/date'
import useStore from '../../store'
import { SessionSlice } from '../../store/session'
import useFilters from '../../hooks/filters'
import PreBillingsFilters from '../../components/pre-bilings/filters'
import { FiltersResourcesEnum } from '../../store/filters'
import ListPageLayout from '../../components/layout/list-page-layout'
import { usePeriodSummariesQuery } from '../../queries/period-summaries'
import { WarningIcon } from '../../components/icons'

const PreBillingsList: React.FC = () => {
  const currentUserRole = useStore(({ session }) => session.currentUserRole)
  const { filters, setFilters } = useFilters(FiltersResourcesEnum.preBillings)
  const tableColumns = useListTableColumns(currentUserRole)

  const periodSummariesQuery = usePeriodSummariesQuery({
    'createdAt>': filters.start || null,
    'createdAt<': filters.end || null,
    contractModel: 'threeParties',
    invoiceRef: filters.invoiceRef,
    interimAgency: filters.interimAgency,
    status: filters.status?.length ? filters.status : undefined,
    sort: '-createdAt',
  })

  // Dedicated query counting every pending cancellation request, independent of current filters
  const pendingCancellationsQuery = usePeriodSummariesQuery(
    {
      contractModel: 'threeParties',
      status: [PeriodSummaryStatus.cancellationRequested],
    },
    { staleTime: 30_000 },
  )
  const pendingCount = pendingCancellationsQuery.data?.length ?? 0

  // Hide the banner CTA when the user is already filtering exclusively on cancellation requests
  const isFilteringOnPendingOnly =
    filters.status?.length === 1 && filters.status[0] === PeriodSummaryStatus.cancellationRequested
  const showPendingButton = pendingCount > 0 && !isFilteringOnPendingOnly

  /**
   * Apply filters so the list only displays pre-billings with a pending cancellation request.
   * invoiceRef is reset to avoid hiding already-invoiced cancellation requests.
   */
  const handleSeePendingCancellations = (): void => {
    setFilters(FiltersResourcesEnum.preBillings, {
      ...filters,
      status: [PeriodSummaryStatus.cancellationRequested],
      invoiceRef: undefined,
    })
  }

  const preBillings = periodSummariesQuery.data || []
  const totalCost = preBillings.reduce<number>(
    (total, preBilling) => (total += preBilling.data?.costs?.total ?? 0),
    0,
  )

  const action = (
    <Level>
      {showPendingButton && (
        <Level.Item>
          <Button
            color="warning"
            size="small"
            onClick={handleSeePendingCancellations}
            data-test="see-pending-cancellations-button"
          >
            <Icon>
              <WarningIcon />
            </Icon>
            <span>
              {`${pendingCount} pré-facturation${
                pendingCount > 1 ? 's' : ''
              } avec demande d'annulation`}
            </span>
          </Button>
        </Level.Item>
      )}
      <Level.Item>
        <Element textSize={5}>
          <Element renderAs="span">Total : </Element>
          <Element renderAs="strong" data-test="total-cost">
            {totalCost.toFixed(2)} €
          </Element>
        </Element>
      </Level.Item>
    </Level>
  )

  return (
    <ListPageLayout
      title="Liste des Pré-Facturations"
      subtitle="Un Employeur génère une Pré-facturation via son Récapitulatif"
      action={action}
      filters={<PreBillingsFilters />}
    >
      <Table
        columns={tableColumns}
        data={preBillings}
        noDataMessage="Aucune pré-facturation à afficher"
      />
    </ListPageLayout>
  )
}

export default PreBillingsList

const useListTableColumns = (
  currentUserRole: SessionSlice['currentUserRole'],
): Column<PeriodSummary>[] => {
  return React.useMemo(
    () => [
      {
        Header: '#',
        accessor: 'identifier',
        Cell: data => (
          <ResourceLinkCell
            resource={data.row.original}
            path="/pre-billings/"
            label={data.row.original.identifier}
          />
        ),
      },
      {
        Header: currentUserRole === 'interimAgencyMember' ? 'Employeur' : 'Agence',
        accessor: currentUserRole === 'interimAgencyMember' ? 'employer' : 'interimAgency',
        Cell: (data: any) =>
          currentUserRole === 'interimAgencyMember'
            ? data.row.original.employer.name
            : data.row.original.interimAgency.name,
      },
      {
        Header: 'Statut',
        id: 'status',
        accessor: row => row.status,
        Cell: ({ row }: { row: { original: PeriodSummary } }) => {
          const status = row.original.status ?? PeriodSummaryStatus.active
          const label =
            status === PeriodSummaryStatus.active
              ? 'Actif'
              : status === PeriodSummaryStatus.cancellationRequested
                ? 'Annulation demandée'
                : 'Annulé'
          const color =
            status === PeriodSummaryStatus.active
              ? 'success'
              : status === PeriodSummaryStatus.cancellationRequested
                ? 'warning'
                : 'dark'
          return <Tag color={color}>{label}</Tag>
        },
      },
      {
        Header: 'N° Facture',
        //@ts-ignore
        accessor: 'invoiceRef',
      },
      {
        Header: 'Semaine',
        //@ts-ignore
        accessor: 'period.week',
      },
      {
        Header: 'Créé le',
        accessor: 'createdAt',
        Cell: data => localDate(data.row.original.createdAt),
      },
      {
        Header: 'Coût',
        //@ts-ignore
        accessor: 'data.costs.total',
        //@ts-ignore
        Cell: data => `${data.row.original.data.costs.total} €`,
      },
    ],
    [],
  )
}
